<template>
    <vx-table-row data-test="externalList-row">
        <vx-table-row-title class="md:col-span-full! lg:col-auto!">
            {{ externalList.name }}
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Contacts: </template>
            <font-awesome-icon
                widthAuto
                v-if="contactsCount[externalList.id] == undefined"
                :icon="faLoader"
                spin
            ></font-awesome-icon>
            <template v-else>
                <span data-test="contacts-count">
                    {{ formatCount(contactsCount[externalList.id]) }}
                </span>
            </template>
        </vx-table-row-item>

        <vx-table-row-item v-if="FEATURES.contact_groups">
            <template v-slot:label> Groups: </template>
            <font-awesome-icon
                widthAuto
                v-if="groupCounts[externalList.id] === undefined"
                :icon="faLoader"
                spin
            ></font-awesome-icon>
            <div v-else data-test="groups-count">
                <span v-if="groupCounts[externalList.id] === null">-</span>
                <meta-badge v-else :icon="faGrid2Plus" color="slate">
                    {{ groupCounts[externalList.id] }}
                </meta-badge>
            </div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Vendor: </template>
            <div>{{ externalListVendor(externalList.vendor) }}</div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            <div>{{ statusLabel[externalList.status] || 'Unknown' }}</div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Created at: </template>

            <vx-popover
                hover
                :arrow="true"
                placement="top"
                :key="externalList.created_at"
                :class="dateTextColor(externalList.created_at)"
            >
                {{ fromNow(externalList.created_at) }}
                <template v-slot:content>
                    {{ shortDateTime(externalList.created_at) }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button
                outline
                color="muted"
                :href="`/messaging/quick-blast?type=marketing&to=external-contact-lists&external_contact_list_ids%5B%5D=${externalList.id}`"
            >
                Send Quick Blast
                <font-awesome-icon widthAuto :icon="faRocketLaunch"></font-awesome-icon>
            </vx-button>
            <vx-popover v-if="FEATURES.contact_groups" arrow placement="top" hover>
                <vx-button
                    :href="`/contacts/external-contact-lists/${externalList.id}/access`"
                    data-test="edit"
                    color="muted"
                    outline
                    :disabled="externalList.status !== 'connected'"
                >
                    Edit
                    <font-awesome-icon widthAuto :icon="faFilePen"></font-awesome-icon>
                </vx-button>
                <template v-if="externalList.status !== 'connected'" v-slot:content>
                    This list has been disconnected.
                </template>
            </vx-popover>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import { inject } from 'vue';
import {
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
    VxTableRowButtons,
    VxPopover,
    VxButton,
} from '@voxie/frontend-components';
import { fromNow, shortDateTime, dateTextColor } from '~/utils/date';
import { faLoader, faFilePen, faGrid2Plus, faRocketLaunch } from '@fortawesome/pro-solid-svg-icons';
import { externalListVendor, formatCount } from '~/components/filters';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useExternalListsContactCount } from '~/composables/useExternalListsContactCount';
import { useExternalListsGroupsCount } from '~/composables/useExternalListsGroupsCount';
import MetaBadge from '~/components/general/MetaBadge.vue';

const FEATURES = inject('FEATURES');

const { contactsCount } = useExternalListsContactCount();
const { groupCounts } = useExternalListsGroupsCount();

defineProps({
    externalList: {
        type: Object,
        required: true,
    },
});

const statusLabel = {
    connected: 'Connected',
    disconnected: 'Disconnected',
};
</script>
